import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/LoginPage.css';

export function LoginPage(): JSX.Element {
  const { loginAsGuest, loginAsAdmin } = useAuth();
  const navigate = useNavigate();

  const [showAdminForm, setShowAdminForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleGuest(): void {
    loginAsGuest();
    navigate('/categories', { replace: true });
  }

  async function handleAdminSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setError(null);
    setIsLoading(true);
    try {
      await loginAsAdmin({ username: username.trim(), password });
      navigate('/categories', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה בהתחברות');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="login-page">
      {/* Background orbs */}
      <div className="login-orb login-orb--1" aria-hidden="true" />
      <div className="login-orb login-orb--2" aria-hidden="true" />
      <div className="login-orb login-orb--3" aria-hidden="true" />

      <div className="login-card">
        {/* Logo / title */}
        <div className="login-logo">
          <img src="/logo.png" alt="מטעמי העיר" className="login-logo__img" />
          <p className="login-logo__sub">מערכת ניהול קטלוג</p>
        </div>

        {!showAdminForm ? (
          /* ── Mode selection ── */
          <div className="login-options">
            <p className="login-options__title">בחר סוג כניסה</p>

            <button className="login-option-btn login-option-btn--guest" onClick={handleGuest}>
              <span className="login-option-btn__icon">👤</span>
              <div className="login-option-btn__text">
                <strong>כניסת אורח</strong>
                <span>צפייה בקטלוג ללא עריכה</span>
              </div>
              <span className="login-option-btn__arrow">←</span>
            </button>

            <button
              className="login-option-btn login-option-btn--admin"
              onClick={() => setShowAdminForm(true)}
            >
              <span className="login-option-btn__icon">🔑</span>
              <div className="login-option-btn__text">
                <strong>כניסת מנהל</strong>
                <span>גישה מלאה לניהול המערכת</span>
              </div>
              <span className="login-option-btn__arrow">←</span>
            </button>
          </div>
        ) : (
          /* ── Admin login form ── */
          <form className="login-form" onSubmit={handleAdminSubmit} noValidate>
            <div className="login-form__header">
              <button
                type="button"
                className="login-back-btn"
                onClick={() => { setShowAdminForm(false); setError(null); }}
                aria-label="חזרה"
              >
                ›
              </button>
              <h2 className="login-form__title">כניסת מנהל</h2>
            </div>

            {error && (
              <div className="login-error" role="alert">
                ⚠ {error}
              </div>
            )}

            <div className="login-field">
              <label className="login-label" htmlFor="username">שם משתמש</label>
              <input
                id="username"
                type="text"
                className="login-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
                dir="ltr"
                placeholder="admin"
              />
            </div>

            <div className="login-field">
              <label className="login-label" htmlFor="password">סיסמה</label>
              <div className="login-input-wrap">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="login-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  dir="ltr"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="login-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                  tabIndex={-1}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-submit-btn"
              disabled={isLoading || !username.trim() || !password}
            >
              {isLoading ? (
                <span className="login-spinner" aria-hidden="true" />
              ) : (
                'התחבר'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
